/**
 * reCAPTCHA v3 de las altas públicas (G76; cabecera `X-Captcha-Token` del contrato). La clave del sitio llega al
 * construir el panel (`VITE_RECAPTCHA_SITE_KEY`, que `scripts/desplegar.sh` toma de `RECAPTCHA_SITE_KEY`). La primera
 * vez que se pide un token se carga, una sola vez, `https://www.google.com/recaptcha/api.js?render=<clave>`; después se
 * espera a `grecaptcha.ready` y se pide el token para la acción. Sin clave (desarrollo y pruebas) no hay token y la API
 * responde `422 captcha_required`, que la pantalla cuenta. Las acciones que se mandan deben estar admitidas en el
 * servidor (`services.recaptcha.acciones`): ver `ACCIONES_CAPTCHA`.
 */
interface ReCaptcha {
    ready: (listo: () => void) => void;
    execute: (clave: string, opciones: { action: string }) => Promise<string>;
}

/** Acciones que manda el panel; el servidor las admite todas en `config/services.php` (`recaptcha.acciones`). */
export const ACCIONES_CAPTCHA = ['alta', 'partner', 'alta_negocio', 'codigo_cliente', 'comprar_entradas', 'pedir_cita', 'reservar'] as const;

const URL_SCRIPT = 'https://www.google.com/recaptcha/api.js?render=';

let cargando: Promise<ReCaptcha | null> | null = null;

function grecaptcha(): ReCaptcha | undefined {
    return (window as unknown as { grecaptcha?: ReCaptcha }).grecaptcha;
}

/** Carga el script de Google una sola vez (las llamadas siguientes reutilizan la misma carga). */
function cargarGoogle(clave: string): Promise<ReCaptcha | null> {
    const ya = grecaptcha();
    if (ya !== undefined) {
        return Promise.resolve(ya);
    }
    cargando ??= new Promise<ReCaptcha | null>((listo) => {
        const script = document.createElement('script');
        script.src = URL_SCRIPT + encodeURIComponent(clave);
        script.async = true;
        script.defer = true;
        script.onload = () => {
            listo(grecaptcha() ?? null);
        };
        script.onerror = () => {
            // Si falla (sin red, bloqueado), se podrá reintentar en el siguiente envío.
            script.remove();
            cargando = null;
            listo(null);
        };
        document.head.appendChild(script);
    });
    return cargando;
}

export async function tokenCaptcha(accion: string): Promise<string | null> {
    const env = import.meta.env as Record<string, string | undefined>;
    const clave = env.VITE_RECAPTCHA_SITE_KEY ?? '';
    if (clave === '' || typeof window === 'undefined') {
        return null;
    }
    const google = await cargarGoogle(clave);
    if (google === null) {
        return null;
    }
    await new Promise<void>((listo) => {
        google.ready(listo);
    });
    return google.execute(clave, { action: accion }).catch(() => null);
}

/** Solo para pruebas: olvida la carga del script. */
export function reiniciarCaptchaParaPruebas(): void {
    cargando = null;
}
